import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR_PRESETS = [
  { label: 'Emerald',   bg: '#10B981', text: '#FFFFFF' },
  { label: 'Navy',      bg: '#16304a', text: '#FFFFFF' },
  { label: 'Teal',      bg: '#1d6f6a', text: '#FFFFFF' },
  { label: 'Gold',      bg: '#c79a3a', text: '#FFFFFF' },
  { label: 'Amber',     bg: '#F59E0B', text: '#1a1a1a' },
  { label: 'Red',       bg: '#EF4444', text: '#FFFFFF' },
  { label: 'Dark',      bg: '#1a1a1a', text: '#FFFFFF' },
  { label: 'Custom',    bg: '',        text: '' },
];

const PAGE_OPTIONS = [
  { value: 'all',                     label: 'All pages' },
  { value: 'home',                    label: 'Homepage (/)' },
  { value: 'newsroom',                label: 'Newsroom' },
  { value: 'coverage/education',      label: 'Education' },
  { value: 'coverage/healthcare',     label: 'Healthcare' },
  { value: 'coverage/business-finance', label: 'Business & Finance' },
  { value: 'coverage/government',     label: 'Government' },
  { value: 'coverage/technology-ai',  label: 'Technology & AI' },
  { value: 'coverage/workforce-labor', label: 'Workforce & Labor' },
  { value: 'career-hub',              label: 'Career Hub' },
];

const EMPTY_FORM = {
  text: '', emoji: '', link_url: '', link_text: '',
  background_color: '#10B981', text_color: '#FFFFFF',
  priority: 1, is_active: true,
  starts_at: '', ends_at: '',
  pages: ['all'], dismissible: false,
};

function statusBadge(ann) {
  const now = new Date();
  if (!ann.is_active) return { label: 'Inactive', color: '#6b7280' };
  const start = ann.starts_at ? new Date(ann.starts_at) : null;
  const end   = ann.ends_at   ? new Date(ann.ends_at)   : null;
  if (end && end < now) return { label: 'Expired', color: '#EF4444' };
  if (start && start > now) return { label: 'Scheduled', color: '#F59E0B' };
  return { label: 'Active', color: '#10B981' };
}

function getAuthHeader() {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function AnnouncementBarSettings() {
  const [messages, setMessages] = useState([]);
  const [editing, setEditing]   = useState(null);   // null | 'new' | id
  const [form, setForm]         = useState(EMPTY_FORM);
  const [saving, setSaving]     = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/api/announcements`, { headers: getAuthHeader() });
      const data = Array.isArray(r.data) ? r.data : [];
      setMessages(data.sort((a, b) => a.priority - b.priority));
    } catch { toast.error('Failed to load announcements'); }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => {
    setForm({ ...EMPTY_FORM });
    setEditing('new');
  };

  const startEdit = (ann) => {
    setForm({
      text:             ann.text || '',
      emoji:            ann.emoji || '',
      link_url:         ann.link_url || '',
      link_text:        ann.link_text || '',
      background_color: ann.background_color || '#10B981',
      text_color:       ann.text_color || '#FFFFFF',
      priority:         ann.priority ?? 1,
      is_active:        ann.is_active ?? true,
      starts_at:        ann.starts_at ? ann.starts_at.slice(0, 16) : '',
      ends_at:          ann.ends_at   ? ann.ends_at.slice(0, 16)   : '',
      pages:            ann.pages || ['all'],
      dismissible:      ann.dismissible ?? false,
    });
    setEditing(ann.id);
  };

  const cancel = () => { setEditing(null); setForm(EMPTY_FORM); };

  const save = async () => {
    if (!form.text.trim()) { toast.error('Text is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        starts_at: form.starts_at ? new Date(form.starts_at).toISOString() : null,
        ends_at:   form.ends_at   ? new Date(form.ends_at).toISOString()   : null,
        pages:     form.pages.length ? form.pages : ['all'],
      };
      if (editing === 'new') {
        await axios.post(`${API}/api/announcements`, payload, { headers: getAuthHeader() });
        toast.success('Announcement created');
      } else {
        await axios.put(`${API}/api/announcements/${editing}`, payload, { headers: getAuthHeader() });
        toast.success('Announcement updated');
      }
      await load();
      cancel();
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const del = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`${API}/api/announcements/${id}`, { headers: getAuthHeader() });
      toast.success('Deleted');
      await load();
    } catch { toast.error('Delete failed'); }
  };

  const toggleActive = async (ann) => {
    try {
      await axios.put(`${API}/api/announcements/${ann.id}`,
        { ...ann, is_active: !ann.is_active, starts_at: ann.starts_at || null, ends_at: ann.ends_at || null },
        { headers: getAuthHeader() }
      );
      await load();
    } catch { toast.error('Toggle failed'); }
  };

  const setPage = (val) => {
    setForm(f => {
      if (val === 'all') return { ...f, pages: ['all'] };
      const cur = f.pages.filter(p => p !== 'all');
      return cur.includes(val)
        ? { ...f, pages: cur.filter(p => p !== val) || ['all'] }
        : { ...f, pages: [...cur, val] };
    });
  };

  const applyPreset = (preset) => {
    if (preset.bg) setForm(f => ({ ...f, background_color: preset.bg, text_color: preset.text }));
  };

  const preview = (
    <div style={{
      width: '100%', minHeight: 44, background: form.background_color, color: form.text_color,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'DM Sans', sans-serif", fontSize: 13.5, fontWeight: 500,
      borderRadius: 0, padding: '0 16px', gap: 8, flexWrap: 'wrap',
    }}>
      {form.emoji && <span>{form.emoji}</span>}
      <span>{form.text || 'Preview text will appear here…'}</span>
      {form.link_text && <span style={{ fontWeight: 700, textDecoration: 'underline' }}>{form.link_text}</span>}
      {form.dismissible && <span style={{ opacity: 0.6, marginLeft: 8 }}>×</span>}
    </div>
  );

  return (
    <AdminLayout>
      <div style={{ maxWidth: 880, margin: '0 auto', padding: '32px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#16304a', margin: 0 }}>Announcement Bar</h1>
            <p style={{ color: '#6b7280', fontSize: 13, marginTop: 4, marginBottom: 0 }}>
              Messages render above the site header on all public pages.
            </p>
          </div>
          {editing === null && (
            <button onClick={startNew} data-testid="ann-add-btn"
              style={{ background: '#16304a', color: '#fff', border: 'none', borderRadius: 6,
                       padding: '9px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
              + Add Message
            </button>
          )}
        </div>

        {/* ── Editor form ── */}
        {editing !== null && (
          <div data-testid="ann-editor" style={{ background: '#f9fafb', border: '1px solid #e5e7eb',
               borderRadius: 8, padding: 24, marginBottom: 28 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: '#16304a', marginTop: 0, marginBottom: 20 }}>
              {editing === 'new' ? 'New Announcement' : 'Edit Announcement'}
            </h2>

            {/* Live Preview */}
            <div style={{ marginBottom: 20, borderRadius: 4, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
              {preview}
            </div>

            {/* Fields */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={lbl}>Message Text *</label>
                <input data-testid="ann-text-input" style={inp}
                  value={form.text} onChange={e => setForm(f => ({ ...f, text: e.target.value }))}
                  placeholder="Announcement text…" />
              </div>
              <div>
                <label style={lbl}>Emoji (optional)</label>
                <input style={{ ...inp, width: 80 }} value={form.emoji}
                  onChange={e => setForm(f => ({ ...f, emoji: e.target.value }))} placeholder="🎩" />
              </div>
              <div>
                <label style={lbl}>Priority (1 = highest)</label>
                <input type="number" min={1} max={99} style={{ ...inp, width: 80 }}
                  value={form.priority}
                  onChange={e => setForm(f => ({ ...f, priority: parseInt(e.target.value) || 1 }))} />
              </div>
              <div>
                <label style={lbl}>Link URL (optional)</label>
                <input style={inp} value={form.link_url}
                  onChange={e => setForm(f => ({ ...f, link_url: e.target.value }))}
                  placeholder="https://… or /path" />
              </div>
              <div>
                <label style={lbl}>Link Text (optional)</label>
                <input style={inp} value={form.link_text}
                  onChange={e => setForm(f => ({ ...f, link_text: e.target.value }))}
                  placeholder="Learn more →" />
              </div>
              <div>
                <label style={lbl}>Starts At (UTC, optional)</label>
                <input type="datetime-local" style={inp} value={form.starts_at}
                  onChange={e => setForm(f => ({ ...f, starts_at: e.target.value }))} />
              </div>
              <div>
                <label style={lbl}>Ends At (UTC, optional)</label>
                <input type="datetime-local" style={inp} value={form.ends_at}
                  onChange={e => setForm(f => ({ ...f, ends_at: e.target.value }))} />
              </div>
            </div>

            {/* Color presets */}
            <div style={{ marginTop: 18 }}>
              <label style={lbl}>Color Scheme</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                {COLOR_PRESETS.filter(p => p.bg).map(p => (
                  <button key={p.label} onClick={() => applyPreset(p)}
                    title={p.label}
                    style={{ width: 28, height: 28, borderRadius: 4, background: p.bg, border: '2px solid',
                             borderColor: form.background_color === p.bg ? '#16304a' : '#e5e7eb',
                             cursor: 'pointer' }} />
                ))}
              </div>
              <div style={{ display: 'flex', gap: 16 }}>
                <div>
                  <label style={{ ...lbl, display: 'inline' }}>Background </label>
                  <input type="color" value={form.background_color}
                    onChange={e => setForm(f => ({ ...f, background_color: e.target.value }))}
                    style={{ width: 40, height: 28, border: 'none', cursor: 'pointer', padding: 0 }} />
                  <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 6 }}>{form.background_color}</span>
                </div>
                <div>
                  <label style={{ ...lbl, display: 'inline' }}>Text </label>
                  <input type="color" value={form.text_color}
                    onChange={e => setForm(f => ({ ...f, text_color: e.target.value }))}
                    style={{ width: 40, height: 28, border: 'none', cursor: 'pointer', padding: 0 }} />
                  <span style={{ fontSize: 12, color: '#6b7280', marginLeft: 6 }}>{form.text_color}</span>
                </div>
              </div>
            </div>

            {/* Page targeting */}
            <div style={{ marginTop: 18 }}>
              <label style={lbl}>Show on pages</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {PAGE_OPTIONS.map(o => {
                  const sel = form.pages.includes(o.value);
                  return (
                    <button key={o.value} onClick={() => setPage(o.value)}
                      style={{ padding: '4px 10px', borderRadius: 4, fontSize: 12, cursor: 'pointer',
                               background: sel ? '#16304a' : '#fff',
                               color: sel ? '#fff' : '#374151',
                               border: '1px solid ' + (sel ? '#16304a' : '#d1d5db'),
                               fontWeight: sel ? 600 : 400 }}>
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Toggles */}
            <div style={{ display: 'flex', gap: 24, marginTop: 18 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={form.is_active}
                  onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                Active
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={form.dismissible}
                  onChange={e => setForm(f => ({ ...f, dismissible: e.target.checked }))} />
                Dismissible (shows × button)
              </label>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button data-testid="ann-save-btn" onClick={save} disabled={saving}
                style={{ background: '#16304a', color: '#fff', border: 'none', borderRadius: 6,
                         padding: '9px 20px', fontWeight: 600, fontSize: 13, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button onClick={cancel}
                style={{ background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db',
                         borderRadius: 6, padding: '9px 16px', fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* ── Message list ── */}
        {messages.length === 0 && editing === null && (
          <p style={{ color: '#9ca3af', textAlign: 'center', padding: 40 }}>No announcements yet. Add one above.</p>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {messages.map(ann => {
            const badge = statusBadge(ann);
            return (
              <div key={ann.id} data-testid={`ann-row-${ann.id}`}
                style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden',
                         background: '#fff', opacity: editing && editing !== ann.id ? 0.5 : 1 }}>
                {/* Preview strip */}
                <div style={{ minHeight: 36, background: ann.background_color, color: ann.text_color,
                              display: 'flex', alignItems: 'center', paddingLeft: 16, gap: 8,
                              fontFamily: "'DM Sans', sans-serif", fontSize: 13, fontWeight: 500 }}>
                  {ann.emoji && <span>{ann.emoji}</span>}
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ann.text}
                  </span>
                </div>
                {/* Meta row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px',
                              fontSize: 12, color: '#6b7280' }}>
                  <span style={{ background: badge.color, color: '#fff', borderRadius: 3,
                                 padding: '2px 7px', fontSize: 11, fontWeight: 600 }}>
                    {badge.label}
                  </span>
                  <span>Priority {ann.priority}</span>
                  <span>Pages: {(ann.pages || ['all']).join(', ')}</span>
                  {ann.starts_at && <span>From {ann.starts_at.slice(0, 10)}</span>}
                  {ann.ends_at   && <span>Until {ann.ends_at.slice(0, 10)}</span>}
                  {ann.dismissible && <span>Dismissible</span>}
                  <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    <button onClick={() => toggleActive(ann)}
                      style={{ ...smBtn, background: ann.is_active ? '#fef3c7' : '#dcfce7',
                               color: ann.is_active ? '#92400e' : '#166534' }}>
                      {ann.is_active ? 'Deactivate' : 'Activate'}
                    </button>
                    <button data-testid={`ann-edit-${ann.id}`} onClick={() => startEdit(ann)}
                      style={{ ...smBtn, background: '#eff6ff', color: '#1d4ed8' }}>
                      Edit
                    </button>
                    <button data-testid={`ann-delete-${ann.id}`} onClick={() => del(ann.id)}
                      style={{ ...smBtn, background: '#fef2f2', color: '#dc2626' }}>
                      Delete
                    </button>
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
}

const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 5 };
const inp = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 5,
              fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' };
const smBtn = { border: 'none', borderRadius: 4, padding: '3px 9px', fontSize: 11,
                fontWeight: 600, cursor: 'pointer' };
